import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "devextreme/dist/css/dx.light.css";
import "./CreateEditEnvironment.scss";
import FileUploader from "devextreme-react/file-uploader";
import { Switch } from "devextreme-react/switch";
import { DropDownBox } from "devextreme-react/drop-down-box";
import ScrollView from "devextreme-react/scroll-view";
import PropTypes from "prop-types";
import { Button } from "devextreme-react/button";
import TextBox from "devextreme-react/text-box";
import TextArea from "devextreme-react/text-area";
import TreeView from "devextreme-react/tree-view";
import CustomStore from "devextreme/data/custom_store";
import classNames from "classnames";
import { ErrorMessages, Placeholders } from "../../Constants/constants";
import {
  dropCertificateSVG,
  certificateFileSVG,
  removeCerSVG,
  certificateInfoSVG,
  closeIconSVG,
  copyClientIDSVG,
  envCopyButtonSVG,
} from "../../Assets/SVGs/environmentSVGs";
import { useUser } from "../../contexts/UserContext";
import Notification from "../Notification/Notification";
import { environmentPropTypes } from "../../Constants/propTypes";
import { notifyMultipleErrors, getLengthOfFilename } from "../../utilities";
import envValidations from "./environmentValidations";

const currentDate = new Date();
const maxDescriptionLength = 500;
const maxEnvironmentNameLength = 50;

const envErrorMessages = {
  name: {
    error: false,
    message: "Environment Name must be in between 3 and 50 characters length",
  },
  url: {
    error: false,
    message: "Please enter a valid url in (https://www.test.com) format",
  },
  description: {
    error: false,
    message: "Description must be in between 3 and 500 characters length",
  },
  filetypes: {
    error: false,
    message: "Please select atleast one file type",
  },
};

const CreateEditEnvironment = ({
  mode,
  setMode,
  environmentData,
  setEnvironmentData,
  orgId,
  closePopup,
  setIsLoadingData,
  setCustomStore,
  sendRequest,
}) => {
  const [clientId, setClientId] = useState(null);
  const [environmentName, setEnvironmentName] = useState("");
  const [environmentDescription, setEnvironmentDescription] = useState("");
  const [environmentAccessUrl, setEnvironmentAccessUrl] = useState("");
  const [environmentState, setEnvironmentState] = useState(false);
  const [fileTypes, setFileTypes] = useState([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [certificateDetails, setCertificateDetails] = useState(null);
  const [displayCertificateDetails, setDisplayCertificateDetails] = useState(false);
  const [expiryDays, setExpiryDays] = useState(0);
  const [isValidCert, setIsValidCert] = useState(false);
  const [saveDisable, setSaveDisable] = useState(true);
  const [errorMessages, setErrorMessages] = useState(envErrorMessages);

  const treeView = useRef(null);

  const { backend } = useUser();

  const stateAttribute = useMemo(
    () => ({
      class: environmentState
        ? ".create-env-form .dx-switch-inner , .create-env-form .dx-switch-handle "
        : ".create-env-form .dx-switch-on-value .dx-switch-inner, .create-env-form .dx-switch-inner , .create-env-form .dx-switch-handle ",
    }),
    [environmentState]
  );

  useEffect(() => {
    backend.callDataReceiptFileTypes().then((res) => {
      setFileTypes(res.data);
    });
  }, [backend]);

  useEffect(() => {
    if (mode === "Edit" && environmentData) {
      const {
        appRegistrationId,
        name,
        description,
        accessUrl,
        active,
        supportedDataReceiptTypes,
        certificates,
      } = environmentData;

      setClientId(appRegistrationId);
      setEnvironmentName(name);
      setEnvironmentDescription(description);
      setEnvironmentAccessUrl(accessUrl);
      setEnvironmentState(active);
      setSelectedFileTypes(supportedDataReceiptTypes);
      setCertificateDetails(certificates[0]);
      setCertificate({ name: certificates[0]?.commonName });
      setDisplayCertificateDetails(true);
      setIsValidCert(true);
    }
  }, [environmentData, mode]);

  useEffect(() => {
    if (certificateDetails) {
      const calculateDays = () => {
        const endDate = new Date(certificateDetails?.endDateTime);
        const diffInMilliseconds = endDate.getTime() - currentDate.getTime();
        const days = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));

        setExpiryDays(days);
      };
      calculateDays();
    }
  }, [certificateDetails]);

  const validateInputs = (type, value) => {
    const isInputValid = envValidations[type](value);

    setErrorMessages((previousErrorStatus) => ({
      ...previousErrorStatus,
      [type]: {
        ...previousErrorStatus[type],
        error: !isInputValid,
      },
    }));
  };

  const renderErrorBlock = (errObj) => (
    <div className="field-err-msg">{errObj.error && errObj.message}</div>
  );

  const formatDate = (dateData) => {
    const dateObj = new Date(dateData);
    const options = { year: "numeric", month: "short", day: "numeric" };

    return dateObj.toLocaleDateString("en-US", options);
  };

  const syncTreeViewSelection = useCallback(
    (e) => {
      const treeViewInstance = treeView?.current?.instance;

      if (treeViewInstance) {
        if (e?.value === null) {
          treeViewInstance.unselectAll();
          setSelectedFileTypes([]);
          validateInputs("filetypes", 0);
        } else {
          const values = e?.value || selectedFileTypes;
          values?.forEach((value) => {
            treeViewInstance.selectItem(value);
          });
        }
      }

      if (!e) {
        treeViewInstance?.unselectAll();
      }
    },
    [selectedFileTypes]
  );

  const onFileTypeSelectionChange = useCallback((e) => {
    const activeFileTypes = e.component.getSelectedNodeKeys();
    validateInputs("filetypes", activeFileTypes?.length);

    setSelectedFileTypes(activeFileTypes);
  }, []);

  const renderFileTypes = useCallback(
    () => (
      <TreeView
        ref={treeView}
        dataSource={fileTypes}
        keyExpr="id"
        displayExpr="fileType"
        selectionMode="multiple"
        showCheckBoxesMode="normal"
        selectNodesRecursive={false}
        selectByClick={true}
        onItemSelectionChanged={onFileTypeSelectionChange}
        onContentReady={syncTreeViewSelection}
      />
    ),
    [fileTypes, onFileTypeSelectionChange, syncTreeViewSelection]
  );

  const removeCertificate = () => {
    setCertificate(null);
    setCertificateDetails(null);
    setIsValidCert(false);
    setDisplayCertificateDetails(false);
  };

  const clearFormFields = useCallback(() => {
    setEnvironmentName("");
    setEnvironmentAccessUrl("");
    setEnvironmentDescription("");
    setEnvironmentState(false);
    setSelectedFileTypes([]);
    syncTreeViewSelection();
    setErrorMessages(envErrorMessages);
  }, [syncTreeViewSelection]);

  const handleFileUpload = (e) => {
    const lengthOfFile = getLengthOfFilename(e[0]?.name);
    if (lengthOfFile > 100) {
      Notification("Certificate name must not exceed 100 characters", "error");
      return;
    }
    setCertificate(e[0]);
    setIsValidCert(false);
    setCertificateDetails(null);

    const formData = new FormData();
    formData.append("certFile", e[0]);

    backend
      .callGetCertificateDetails(formData)
      .then((res) => {
        setIsValidCert(true);
        setCertificateDetails(res.data);
        setDisplayCertificateDetails(true);
      })
      .catch(() => {
        removeCertificate();
        Notification(ErrorMessages.ValidateCertificate, "error");
      });
  };

  const compareArrays = (arrX, arrY) => {
    const setA = new Set(arrX);
    const setB = new Set(arrY);

    if (setA.size !== setB.size) return false;

    // eslint-disable-next-line no-restricted-syntax
    for (const element of setA) {
      if (!setB.has(element)) {
        return false;
      }
    }

    return true;
  };

  const isFormSavable = useCallback(
    () =>
      errorMessages.name.error ||
      errorMessages.url.error ||
      errorMessages.description.error ||
      errorMessages.filetypes.error ||
      !isValidCert,
    [errorMessages, isValidCert]
  );

  const isSaveDisabled = useCallback(() => {
    if (mode === "Create") {
      if (
        environmentName.trim() &&
        environmentAccessUrl.trim() &&
        environmentDescription.trim() &&
        selectedFileTypes?.length &&
        certificateDetails?.name &&
        isValidCert
      ) {
        setSaveDisable(isFormSavable);
      } else {
        setSaveDisable(true);
      }
    }

    if (mode === "Edit" && environmentName) {
      if (
        environmentName.trim() !== environmentData?.name ||
        environmentAccessUrl.trim() !== environmentData?.accessUrl ||
        environmentDescription.trim() !== environmentData?.description ||
        environmentState !== environmentData?.active ||
        certificateDetails?.name !== environmentData?.certificates[0]?.name ||
        !compareArrays(selectedFileTypes, environmentData?.supportedDataReceiptTypes)
      ) {
        setSaveDisable(isFormSavable);
      } else {
        setSaveDisable(true);
      }
    }
  }, [
    mode,
    environmentName,
    isValidCert,
    environmentAccessUrl,
    environmentDescription,
    selectedFileTypes,
    certificateDetails,
    isFormSavable,
    environmentData,
    environmentState,
  ]);

  useEffect(() => {
    isSaveDisabled();
  }, [isSaveDisabled]);

  const handlePopupClose = useCallback(() => {
    closePopup();
    setMode("Create");
    setClientId(null);
    clearFormFields();
    removeCertificate();
    setEnvironmentData(null);
    setSaveDisable(true);
  }, [clearFormFields, closePopup, setEnvironmentData, setMode]);

  const handleEnvironmentCreate = useCallback(() => {
    setIsLoadingData(true);

    const envFormData = new FormData();
    envFormData.append("certificateFile", certificate);
    envFormData.append("id", 0);
    envFormData.append("name", environmentName);
    envFormData.append("description", environmentDescription);
    envFormData.append("accessUrl", environmentAccessUrl);
    envFormData.append("active", environmentState);
    envFormData.append("organizationId", orgId);
    selectedFileTypes.forEach((type) => {
      envFormData.append("supportedDataReceiptTypes", type);
    });

    backend
      .callCreateEnvironment(envFormData)
      .then((res) => {
        setClientId(res.data.appId);
        Notification("Environment created successfully", "success");
        setCustomStore(
          new CustomStore({
            load: async () =>
              sendRequest("load", orgId).then((envRes) => envRes.data.environments),
          })
        );
      })
      .catch((err) => {
        if (err?.response?.data?.errors) {
          notifyMultipleErrors(err.response.data.errors);
        } else {
          Notification(err.Message || err.message, "error");
        }
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [
    environmentAccessUrl,
    backend,
    certificate,
    environmentDescription,
    environmentName,
    environmentState,
    orgId,
    selectedFileTypes,
    sendRequest,
    setCustomStore,
    setIsLoadingData,
  ]);

  const handleEnvironmentUpdate = useCallback(() => {
    setIsLoadingData(true);

    const envFormData = new FormData();

    const updatedCertificate = certificate?.size ? certificate : null;

    envFormData.append("id", environmentData.id);
    envFormData.append("organizationId", orgId);
    envFormData.append("name", environmentName);
    envFormData.append("accessUrl", environmentAccessUrl);
    envFormData.append("active", environmentState);
    envFormData.append("description", environmentDescription);
    envFormData.append("certificateFile", updatedCertificate);
    selectedFileTypes.forEach((type) => {
      envFormData.append("supportedDataReceiptTypes", type);
    });

    backend
      .callUpdateEnvironment(environmentData?.id, envFormData)
      .then(() => {
        Notification("Environment updated successfully", "success");
        setCustomStore(
          new CustomStore({
            load: async () =>
              sendRequest("load", orgId).then((envRes) => envRes.data.environments),
          })
        );
        handlePopupClose();
      })
      .catch((err) => {
        if (err?.response?.data?.errors) {
          notifyMultipleErrors(err.response.data.errors);
        } else {
          Notification(err.Message || err.message, "error");
        }
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [
    backend,
    certificate,
    environmentAccessUrl,
    environmentData,
    environmentDescription,
    environmentName,
    environmentState,
    handlePopupClose,
    orgId,
    selectedFileTypes,
    sendRequest,
    setCustomStore,
    setIsLoadingData,
  ]);

  const copyClientId = useCallback(() => {
    navigator.clipboard?.writeText(clientId);
    Notification("Client ID Copied Successfully", "success");
  }, [clientId]);

  const userMessage = () =>
    mode === "Create" ? (
      <div className="instructions">
        <h4 className="head">Before you start</h4>
        <p className="instructs">
          You can use any valid certificate installed on the network client machine, with
          a valid private key, to create an environment in the Partner Portal. You can
          also use a self-signed certificate. Your certificate (public key) must be one of
          the following file types: .cer, .pem, .crt.
        </p>
      </div>
    ) : (
      <div className="clientid-wrapper">
        <div className="clientid-label">Client ID :</div>
        <div className="clientid-value">{clientId}</div>
        <div className="copy-btn-wrapper">
          <Button
            className="copy-clientid-btn"
            onClick={copyClientId}
            stylingMode="text"
            height={20}
            width={20}
          >
            {envCopyButtonSVG}
          </Button>
        </div>
      </div>
    );

  const footerButtons = () => (
    <div className="form-buttons">
      <Button
        id="createeditenvironment-cancel-btn"
        text="Cancel"
        className="btn-outlined"
        onClick={handlePopupClose}
      />
      <Button
        id="createeditenvironment-add-or-save-btn"
        className="btn-default"
        text={mode === "Create" ? mode : "Save"}
        onClick={mode === "Create" ? handleEnvironmentCreate : handleEnvironmentUpdate}
        disabled={saveDisable}
      />
    </div>
  );

  const footerActions = () => {
    let footerContent = footerButtons();

    if (mode === "Create" && clientId) {
      footerContent = (
        <div className="footer-clientid">
          <div className="clientid-label">Client ID : {clientId} </div>
          <div className="clientid-actions">
            <Button className="copy-clientid" onClick={copyClientId} stylingMode="text">
              <span className="copy-icon">{copyClientIDSVG}</span>
            </Button>
            <div className="done-btn-wrapper">
              <Button className="done-btn" onClick={handlePopupClose}>
                Done
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return footerContent;
  };

  return (
    <ScrollView width="100%" height="100%">
      <div
        id="createeditenvironment-popup"
        className="create-edit-environment-popup-content"
      >
        <div className="popup-header">
          <div className="header-title">{mode} environment</div>
          <div className="header-actions">
            <div id="createeditenvironment-popup-close-btn" className="close-action">
              <Button onClick={handlePopupClose} stylingMode="text">
                {closeIconSVG}
              </Button>
            </div>
          </div>
        </div>
        <div className="popup-content">
          <div className="create-edit-environment-form">
            {clientId && mode === "Create" && <div className="overlay" />}
            <div className="user-message-block">{userMessage()}</div>
            <div className="environment-form">
              <div className="form-fields">
                <div className="fields-wrapper">
                  <div className="environment-details">
                    <div className="form-layout two-col-layout">
                      <div className="layout-item left-col">
                        <div className="col-content">
                          <div className="environment-name form-field">
                            <div className="field-title">
                              Environment name{" "}
                              <span className="required-asterisk">*</span>
                            </div>
                            <div className="field-input">
                              <TextBox
                                value={environmentName}
                                id="createeditenvironment-name"
                                placeholder={Placeholders.EnvironmentName}
                                maxLength={maxEnvironmentNameLength}
                                className={classNames({
                                  "error-field": errorMessages.name.error,
                                })}
                                onKeyUp={(e) => {
                                  setEnvironmentName(e?.event?.target?.value);
                                  validateInputs("name", e?.event?.target?.value);
                                }}
                              />
                            </div>
                            {renderErrorBlock(errorMessages.name)}
                          </div>
                          <div className="environment-url form-field">
                            <div className="field-title">
                              Access URL <span className="required-asterisk">*</span>
                            </div>
                            <div className="field-input">
                              <TextBox
                                value={environmentAccessUrl}
                                id="createeditenvironment-access-url"
                                placeholder={Placeholders.URL}
                                className={classNames({
                                  "error-field": errorMessages.url.error,
                                })}
                                onKeyUp={(e) => {
                                  setEnvironmentAccessUrl(e?.event?.target?.value);
                                  validateInputs("url", e?.event?.target?.value);
                                }}
                              />
                            </div>
                            {renderErrorBlock(errorMessages.url)}
                          </div>
                          <div className="environment-state form-field">
                            <div className="field-title">State</div>
                            <div className="field-input field-switch">
                              <div className="switch-label active-toggle">
                                {environmentState ? "Active" : "Inactive"}{" "}
                              </div>
                              <div className="switch-item">
                                <Switch
                                  value={environmentState}
                                  elementAttr={stateAttribute}
                                  dataField="active"
                                  switchedOnText=""
                                  switchedOffText=""
                                  hoverStateEnabled={false}
                                  defaultValue={false}
                                  onValueChange={(e) => setEnvironmentState(e)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="layout-item right-col">
                        <div className="col-content">
                          <div className="environment-description form-field">
                            <div className="field-title">
                              Description <span className="required-asterisk">*</span>
                            </div>
                            <div className="field-input">
                              <TextArea
                                value={environmentDescription}
                                id="createeditenvironment-description"
                                placeholder={Placeholders.Description}
                                maxLength={maxDescriptionLength}
                                className={classNames({
                                  "error-field": errorMessages.description.error,
                                })}
                                onKeyUp={(e) => {
                                  setEnvironmentDescription(e?.event?.target?.value);
                                  validateInputs("description", e?.event?.target?.value);
                                }}
                              />
                              <div className="length-indicator">
                                {environmentDescription?.length}/{maxDescriptionLength}
                              </div>
                            </div>
                            {renderErrorBlock(errorMessages.description)}
                          </div>
                          <div className="environment-file-types form-field">
                            <div className="field-title">
                              Select file types{" "}
                              <span className="required-asterisk">*</span>
                            </div>
                            <div className="field-input field-dropdown">
                              <DropDownBox
                                id="createeditenvironment-select-filetype"
                                value={selectedFileTypes}
                                valueExpr="id"
                                displayExpr="fileType"
                                dataSource={fileTypes}
                                placeholder={Placeholders.File_Types}
                                contentRender={renderFileTypes}
                                onValueChanged={syncTreeViewSelection}
                                showClearButton={true}
                                dropDownOptions={{ height: 200 }}
                                deferRendering={false}
                                className={classNames({
                                  "error-field": errorMessages.filetypes.error,
                                })}
                              />
                            </div>
                            {renderErrorBlock(errorMessages.filetypes)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="certificate-wrapper">
                    <div className="file-uploader">
                      <div className="field-title">
                        Upload file <span className="required-asterisk">*</span>
                      </div>
                      <div
                        id="createeditenvironment-dropzone"
                        className="file-uploader-container"
                      >
                        <div className="upload-file-context">
                          <div className="upload-file-icon">{dropCertificateSVG}</div>
                          <div className="upload-file-text">
                            Drop certificate here or click to upload.
                          </div>
                        </div>
                      </div>

                      <FileUploader
                        id="createeditenvironment-file-uploader"
                        dialogTrigger="#createeditenvironment-dropzone"
                        dropZone="#createeditenvironment-dropzone"
                        onValueChange={handleFileUpload}
                        labelText=""
                        multiple={false}
                        accept="crt,pem,cer"
                        allowedFileExtensions={[".cer", ".pem", ".crt"]}
                        uploadMode="useButtons"
                        visible={false}
                      />
                    </div>

                    <div
                      className={classNames("certificate-details", {
                        "show-certificate-details": certificate?.name,
                      })}
                    >
                      {certificate?.name && (
                        <div className="file-name-size d-flex flex-column">
                          <div className="flex-container size-name-box">
                            <div className="env-upload-file-icon">
                              <span>{certificateFileSVG}</span>
                            </div>
                            <div className="certificate-row d-flex">
                              <div className="certificate-name-size">
                                <span>{certificate.name}</span>
                                {displayCertificateDetails ? (
                                  <span>{certificateDetails?.size}</span>
                                ) : (
                                  <span>Validating ...</span>
                                )}
                              </div>
                              <div className="remove-certificate">
                                <Button onClick={removeCertificate} stylingMode="text">
                                  {removeCerSVG}
                                </Button>
                              </div>
                            </div>
                          </div>
                          {displayCertificateDetails && (
                            <div className="certificate-details-box d-flex  ">
                              <div className="certificate-icon-class">
                                <span>{certificateInfoSVG}</span>
                              </div>
                              <div className="  d-flex dates-box flex-row text-justify">
                                <div className="d-flex cert-start-date flex-column">
                                  <div>
                                    <span className="certificate-bold">Start Date:</span>
                                  </div>
                                  <div>
                                    <span>
                                      {formatDate(certificateDetails?.startDateTime)}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex cert-expiry-date flex-column">
                                  <div>
                                    <span className="certificate-bold">Expiry Date:</span>
                                  </div>
                                  <div className="expiry-days">
                                    <div className="d-inline-block">
                                      <span>
                                        {formatDate(certificateDetails?.endDateTime)}
                                      </span>
                                    </div>
                                    <div className="d-inline-block">
                                      {expiryDays <= 90 && (
                                        <span className="text-danger">
                                          &nbsp;({expiryDays} days remaining)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex cert-thumbprint flex-column">
                                  <div>
                                    <span className="certificate-bold">Thumbprint:</span>
                                  </div>
                                  <div>
                                    <span>{certificateDetails?.thumbprint}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-actions">{footerActions()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ScrollView>
  );
};

CreateEditEnvironment.defaultProps = {
  environmentData: null,
};

CreateEditEnvironment.propTypes = {
  mode: PropTypes.string.isRequired,
  setMode: PropTypes.func.isRequired,
  environmentData: environmentPropTypes,
  setEnvironmentData: PropTypes.func.isRequired,
  orgId: PropTypes.number.isRequired,
  closePopup: PropTypes.func.isRequired,
  setIsLoadingData: PropTypes.func.isRequired,
  setCustomStore: PropTypes.func.isRequired,
  sendRequest: PropTypes.func.isRequired,
};

export default CreateEditEnvironment;
